// src/components/ClaimDetail/ClaimFinancials.jsx
//
// Pestaña "Cobro CSR": lo que corresponde a CSR por cada cheque del modelo
// anterior. Antes vivía debajo del flujo de pasos, compitiendo con el trabajo
// del paso actual; ahora tiene su propia sección. "Continuar" en una tarjeta
// abre el cobro ahí mismo (CsrCollection) y Eliminar pide confirmación.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { deleteCheck, OPERATOR_FEE } from '../../services/claimService';
import ClaimDetailCard from '../Dashboard/ClaimDetailCard';
import CsrCollection from '../Dashboard/CsrCollection';
import ConfirmModal from '../common/ConfirmModal';

const formatCurrency = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

const ClaimFinancials = ({ checks, setChecks, isAdmin, language }) => {
  const es = language === 'es';
  // Cobro de la comisión de un cheque: se abre ahí mismo, debajo de su tarjeta.
  const [openCollectionId, setOpenCollectionId] = useState(null);
  const [pendingDeleteCheck, setPendingDeleteCheck] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalAmount = checks.reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalPending = checks.reduce((sum, c) => sum + (c.pending || 0), 0);
  const totalPaid = totalAmount - totalPending;
  const totalEarning = checks.reduce((sum, c) => {
    const gross = ((c.amount || 0) * (c.adjusterPercentage || 0)) / 100;
    return sum + (gross - (c.totalCollected || 0));
  }, 0);

  const handleDeleteCheck = async () => {
    // Un segundo clic mientras se borra lanzaría otra eliminación y mostraría un error tras un borrado exitoso.
    if (!pendingDeleteCheck || deleting) return;
    setDeleting(true);
    try {
      await deleteCheck(pendingDeleteCheck.id);
      toast.success(es ? 'Cheque eliminado' : 'Check deleted');
      setPendingDeleteCheck(null);
      setChecks(current => current.filter(c => c.id !== pendingDeleteCheck.id));
    } catch (err) {
      console.error(err);
      toast.error(es ? 'No se pudo eliminar' : 'Could not delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="cd-detail-financial" aria-label={es ? 'Cheques y pagos' : 'Checks and payments'}>
      <div className="cd-detail-section-intro">
        <h2>{es ? 'Cobro de comisión CSR' : 'CSR commission collection'}</h2>
        <p>{es
          ? 'Interno: lo que corresponde a CSR por cada cheque. El recorrido de cada cheque del seguro (endoso, depósito, breakdown, Closing, desembolso) está en la pestaña Pasos → Fase 4.'
          : 'Internal: what CSR is owed per check. Each insurance check journey (endorsement, deposit, breakdown, Closing, disbursement) is in the Steps tab → Phase 4.'}</p>
      </div>
      {/* Totales: valor / pagado / pendiente responden "cuánto y cuánto falta".
          "Ganancia ajustador" mezcla otro concepto (comisión, no cobro del
          reclamo), así que va en una línea aparte con su propia explicación. */}
      <div className="cd-detail-totals-bar">
        <div className="cd-detail-total-stat">
          <span className="cdd-stat-label">{es ? 'Valor de cheques' : 'Checks value'}</span>
          <strong className="cd-detail-total-value">{formatCurrency(totalAmount)}</strong>
        </div>
        <div className="cd-detail-total-stat">
          <span className="cdd-stat-label">{es ? 'Pagado' : 'Paid'}</span>
          <strong className="cd-detail-total-value">{formatCurrency(totalPaid)}</strong>
        </div>
        <div className="cd-detail-total-stat">
          <span className="cdd-stat-label">{es ? 'Pendiente' : 'Pending'}</span>
          <strong className="cd-detail-total-value">{formatCurrency(totalPending)}</strong>
        </div>
      </div>

      <div className="cd-detail-earning-line">
        <span className="cd-detail-earning-label">{es ? 'Ganancia ajustador' : 'Adjuster earning'}</span>
        <strong className="cd-detail-earning-value">{formatCurrency(totalEarning)}</strong>
        <span className="cd-detail-earning-hint">
          {es ? '— tu % sobre el monto del cheque, menos lo ya cobrado por CSR.' : '— your % of the check amount, minus what CSR has already collected.'}
        </span>
      </div>

      <div className="cd-detail-checks-header">
        <h2 className="cd-detail-checks-title">{es ? 'Comisión por cheque' : 'Commission per check'}</h2>
        <span className="cd-detail-checks-count">{checks.length} {es ? (checks.length === 1 ? 'cheque' : 'cheques') : (checks.length === 1 ? 'check' : 'checks')}</span>
      </div>
      <div className="cd-detail-checks">
        {checks.map((check, i) => (
          <React.Fragment key={check.id}>
            <ClaimDetailCard
              claim={check}
              index={i}
              isAdmin={isAdmin}
              onContinue={isAdmin ? () => setOpenCollectionId(id => (id === check.id ? null : check.id)) : null}
              onDelete={isAdmin ? () => setPendingDeleteCheck(check) : null}
            />
            {openCollectionId === check.id && (
              <div className="flow-panel claim-inline-collection">
                <CsrCollection check={check} isAdmin={isAdmin} language={language}
                  onCheckChange={fresh => setChecks(current => current.map(c => (c.id === check.id ? { ...c, ...fresh } : c)))} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <ConfirmModal
        open={!!pendingDeleteCheck}
        icon="fa-trash"
        danger
        title={es ? 'Eliminar cheque' : 'Delete check'}
        message={pendingDeleteCheck ? ((pendingDeleteCheck.archived || Number(pendingDeleteCheck.deposit) > 0)
          ? (es
            ? `El invoice #${pendingDeleteCheck.invoiceNumber || ''} ya tiene pagos registrados y generó una deuda de operador de $${OPERATOR_FEE} — al eliminarlo, esa deuda también se borra. ¿Continuar?`
            : `Invoice #${pendingDeleteCheck.invoiceNumber || ''} already has payments recorded and generated a $${OPERATOR_FEE} operator debt — deleting it also deletes that debt. Continue?`)
          : (es ? `¿Eliminar el cheque #${pendingDeleteCheck.invoiceNumber || ''}? Esta acción no se puede deshacer.` : `Delete check #${pendingDeleteCheck.invoiceNumber || ''}? This cannot be undone.`)) : ''}
        confirmLabel={es ? 'Eliminar' : 'Delete'}
        cancelLabel={es ? 'Cancelar' : 'Cancel'}
        onConfirm={handleDeleteCheck}
        onCancel={() => setPendingDeleteCheck(null)}
      />
    </section>
  );
};

export default ClaimFinancials;
